import { useQuery } from '@apollo/client'
import React, { useContext } from 'react'
import styled from 'styled-components'

import { CustomerInvoiceFragment } from '../../../../../graphql/camel'
import { GetOrderProductsDocument } from '../../../../../graphql/magento'
import { TokenContext } from '../../../../../layouts/context'
import { formatAsCurrencyOrZero } from '../../../../../utils/priceTools'
import Spinner from '../../../../Spinner'
import * as Styled from './../../orders/order/OrderItemEntry/styled'

import productPlaceholderImage from '../../../../../img/product-image-placeholder.png'

export interface InvoiceItemEntryProps {
  className?: string
  item: NonNullable<CustomerInvoiceFragment['items'][number]>
  printDiscountColumn: boolean
}

const InvoiceItemEntry: React.FC<InvoiceItemEntryProps> = ({
  className,
  item,
  printDiscountColumn,
}) => {
  const token = useContext(TokenContext)

  // TODO refactor to call getOrderProducts only once
  //  rather than for every item
  //  (see order page for example)
  const { loading, data } = useQuery(GetOrderProductsDocument, {
    context: { token },
    variables: { sku: item.item },
  })

  if (loading) return <Spinner loading />

  const imageUrl = data?.products?.items?.[0]?.image?.url || undefined
  const imageLabel = data?.products?.items?.[0]?.image?.label || undefined
  const productName = data?.products?.items?.[0]?.name || undefined
  const productAttributes =
    data?.products?.items?.[0]?.__typename === 'SimpleProduct' &&
    data?.products?.items?.[0]?.variant_index?.attribute_values
      ? JSON.parse(data.products.items[0].variant_index.attribute_values)
      : {}

  return (
    <div className={className}>
      <Styled.GapContainer>
        <Styled.ItemDetails>
          <Styled.ItemImage
            alt={imageLabel || ''}
            className="item-image"
            src={imageUrl || productPlaceholderImage}
          />
          <Styled.PrintContainer>
            <Styled.ItemName>
              <Styled.ItemSku>SKU: {item.item}</Styled.ItemSku>
              {productName || item.description || `Item with SKU ${item.item}`}
            </Styled.ItemName>
            <Styled.ItemAttribute>
              <Styled.ItemAttributeType>Quantity:</Styled.ItemAttributeType>
              {item.quantity}
            </Styled.ItemAttribute>
            {productAttributes.size && (
              <Styled.ItemAttributeNoPrint>
                Size: {productAttributes.size}
              </Styled.ItemAttributeNoPrint>
            )}
            {productAttributes.dosage && (
              <Styled.ItemAttributeNoPrint>
                Dosage: {productAttributes.dosage}
              </Styled.ItemAttributeNoPrint>
            )}
            {productAttributes.flavor && (
              <Styled.ItemAttributeNoPrint>
                Flavor: {productAttributes.flavor}
              </Styled.ItemAttributeNoPrint>
            )}
            <Styled.ItemAttribute>
              <Styled.ItemAttributeType>Unit Price:</Styled.ItemAttributeType>
              {formatAsCurrencyOrZero({ amount: item.rate })}
            </Styled.ItemAttribute>
            <Styled.ItemAmount>
              <Styled.ItemAttributeType>Amount:</Styled.ItemAttributeType>
              {formatAsCurrencyOrZero({ amount: item.amount })}
            </Styled.ItemAmount>
            {!!item.discountAmount && item.discountAmount > 0 && (
              <Styled.ItemAmount>
                <Styled.ItemAttributeType>Discount:</Styled.ItemAttributeType>
                {formatAsCurrencyOrZero({ amount: item.discountAmount * -1 })}
              </Styled.ItemAmount>
            )}
            {!(item.discountAmount && item.discountAmount > 0) && printDiscountColumn && (
              <Styled.ItemAmountPrintOnly>-</Styled.ItemAmountPrintOnly>
            )}
          </Styled.PrintContainer>
        </Styled.ItemDetails>
      </Styled.GapContainer>
    </div>
  )
}

const StyledInvoiceItemEntry = styled(InvoiceItemEntry)`
  background-color: ${({ theme }) => theme.colors.grayscale.white};
  overflow: hidden;
  padding: 20px;
  @media print {
    display: contents;
  }
`

export default StyledInvoiceItemEntry
